import React, { useState, useCallback, useEffect } from "react";
import { useQuery, useMutation } from '@apollo/client';
import { useSelector, } from 'react-redux';
import { useParams, Link, useHistory } from "react-router-dom";
import { makeDateString, checkValidity } from '../../util.js';
import ProductForm from "./ProductForm.js";
import { baseProductObject } from './BaseProductObject.js';
import confetti from 'canvas-confetti';
import { gql } from '@apollo/client';

import CREATE_PRODUCT_MUTATION from "../../graphql/mutations/Products/CreateProduct.js";
import CREATE_PRODUCT_CREATOR_MUTATION from "../../graphql/mutations/Products/CreateProductCreator.js";
import QUERY_PRODUCT from "../../graphql/queries/Products/QueryProduct.js";
import QUERY_PRODUCTS from "../../graphql/queries/Products/QueryProducts.js";

import {
  Pane,
  Paragraph,
  Button,
  toaster,
  Tooltip,
  HelpIcon
} from 'evergreen-ui';

function NewProduct(props) {
  // Initial state setup - properly invoke baseProductObject with ()
  const getInitialState = () => {
    const fullObject = baseProductObject();

    // If adding format to existing work, remove productWork-related fields
    if (props.productWorkId) {
      // Create a new object with only format-specific fields
      return {
        id: "",
        subtitle: "",
        isbn: "",
        shipDate: "",
        publicationDate: "",
        // Default Status to 'Active' (assuming ID '4')
        status: { id: "4", description: "Active", __typename: "OnixProductAvailabilityType" }, // Add __typename if needed for comparison/cache
        // Default Format to 'Paperback' (assuming ID '2')
        format: { id: "2", description: "Paperback", __typename: "OnixProductFormType" }, // Add __typename if needed
        formatDetail: { ...fullObject.formatDetail },
        retailPrice: "",
        wholesalePrice: "",
        inventoryQuantity: "",
        cartonQty: "",
        weight: {},
        width: {},
        height: {},
        thickness: {},
        pageCount: "",
        audience: { ...fullObject.audience },
        audienceDetails: "",
        adminNotes: "",
        tags: "[]",
        edition: "",
        volume: "",
        productcreatorSet: [...fullObject.productcreatorSet]
      };
    }

    // For new work, ensure defaults are set in the full object
    fullObject.status = { id: "4", description: "Active", __typename: "OnixProductAvailabilityType" };
    fullObject.format = { id: "2", description: "Paperback", __typename: "OnixProductFormType" };

    return fullObject;
  };

  // Initialize state with the function result
  const [staged, setStaged] = useState(() => getInitialState());
  const [stagedCreators, setStagedCreators] = useState([]);
  const currentpublisher = useSelector((state) => state.currentpublisher.value);
  const [changed, setChanged] = useState([]);
  const [deletedCreators, setDeletedCreators] = useState([]);

  const [createProduct, { productdata }] = useMutation(CREATE_PRODUCT_MUTATION, {
    update(cache, { data: { createProduct } }) {
      // Modify the cache for all product queries
      cache.modify({
        fields: {
          products(existingProducts = []) {
            // Check if the actual product data exists in the response
            if (!createProduct || !createProduct.product) {
               console.error("Cannot update cache: Product data missing in mutation response.", createProduct);
               return existingProducts; // Return existing products without modification
            }

            const newProductRef = cache.writeFragment({
              // data: createProduct, // Incorrect: Passing the whole payload
              data: createProduct.product, // Correct: Pass the nested product object
              fragment: gql`
                fragment NewProduct on Product {
                  id
                  isbn
                  format {
                    id
                    description
                    __typename # Add typename for cache consistency
                  }
                  productWork {
                    id
                    title
                    __typename # Add typename
                  }
                  # Add any other fields potentially read by the products query
                  subtitle
                  status { # Add status if it's read elsewhere
                    id
                    description
                    __typename
                  }
                  __typename # Add typename for cache consistency
                }
              `
            });
            // Add the new product reference only if writeFragment succeeded
            return newProductRef ? [newProductRef, ...existingProducts] : existingProducts;
          }
        }
      });
    }
  });
  const [createProductCreator, { productcreatordata }] = useMutation(CREATE_PRODUCT_CREATOR_MUTATION);
  const { productId } = useParams();

  const handleClearStaged = () => {
    setStaged(getInitialState());
  }

  // Adjust canSave logic to account for productWork model
  const canSave = props.productWorkId
    ? (staged.format.id && staged.retailPrice) // When adding format to existing work
    : (staged.title && staged.format.id && staged.retailPrice); // When creating new work

  const handleUpdateStaged = useCallback(obj => {
    if (!obj || !obj.key) return; // Basic validation

    let newValue = obj.val;
    const currentVal = staged[obj.key];

    // Simple comparison for primitive types
    let hasChanged = newValue !== currentVal;

    // Deeper comparison for objects (like from ForeignKeyDropDown)
    if (typeof newValue === 'object' && newValue !== null && typeof currentVal === 'object' && currentVal !== null) {
      // Compare by ID if both have it (common case for dropdowns)
      if (newValue.id !== undefined && currentVal.id !== undefined) {
        hasChanged = newValue.id !== currentVal.id;
      } else {
        // Fallback: Basic object string comparison (less reliable)
        try {
          hasChanged = JSON.stringify(newValue) !== JSON.stringify(currentVal);
        } catch (e) {
           console.error("Error comparing objects:", e);
           hasChanged = true; // Assume changed if stringify fails
        }
      }
    } else if (typeof newValue !== typeof currentVal) {
       // Types are different, consider it changed
       hasChanged = true;
    }

    // Only update state if the value has actually changed
    if (hasChanged) {
        // Process value (e.g., format dates)
        let processedValue = newValue;
        if (obj.key.includes("Date") && processedValue) {
          if (typeof processedValue !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(processedValue)) {
             processedValue = makeDateString(processedValue);
          }
        }

        // Ensure objects passed from ForeignKeyDropDownInput are handled correctly
        // The `handleChange` in ForeignKeyDropDownInput passes { key: props.el, val: selected }
        // So, processedValue here *is* the selected object (e.g., { id: '2', description: 'Paperback', ... })
        setStaged(oldState => ({ ...oldState, [obj.key]: processedValue }));

        // Update changed array (optional, keep if needed)
        const newArr = Array.isArray(changed)
          ? changed.filter(item => item.hasOwnProperty("key") && item.key !== obj.key)
          : [];
        newArr.push({ key: obj.key, val: processedValue }); // Store processed value
        setChanged(newArr);
    }
  }, [staged, changed]);

  const handleCreateProduct = async () => {
    // Ensure format has an id before proceeding
    if (!staged.format || !staged.format.id) {
      toaster.warning("Please select a format for the product.");
      return;
    }

    // Validate based on whether we're adding to existing work or creating new work
    let validationFields = [{ name: "format", required: true, fieldType: "object", field: "id" }];

    // If not linking to existing work, title is required
    if (!props.productWorkId) {
      validationFields.push({ name: "title", required: true });
    }

    let isValid = checkValidity(validationFields, staged);

    if (isValid) {
      try {
        // Prepare base variables
        const variables = {
          publisherId: props.publisher.toString(), // Pass as String for ID type
          formatId: String(staged.format.id), // Changed: Ensure String
        };

        // If adding to existing ProductWork, use its ID
        if (props.productWorkId) {
          variables.productWorkId = String(props.productWorkId); // Changed: Ensure String
        }
        // Otherwise create a new ProductWork with provided details
        else if (staged.title) {
          variables.title = staged.title;
          if (staged.imprint && staged.imprint.id) variables.imprintId = String(staged.imprint.id); // Changed: Ensure String
          if (staged.series && staged.series.id) variables.seriesId = String(staged.series.id); // Changed: Ensure String
          if (staged.description) variables.description = staged.description;
        }

        // Add Product specific fields from staged object, renaming keys where necessary
        if (staged.subtitle) variables.subtitle = staged.subtitle;
        if (staged.edition) variables.edition = staged.edition;
        if (staged.volume) variables.volume = staged.volume;
        if (staged.isbn) variables.isbn = staged.isbn; // Changed back from isbnUid
        if (staged.retailPrice) variables.retailPrice = parseFloat(staged.retailPrice);
        if (staged.inventoryQuantity) variables.inventory = parseInt(staged.inventoryQuantity, 10); // Rename and parse
        if (staged.cartonQty) variables.cartonQty = parseInt(staged.cartonQty, 10);
        if (staged.shipDate) {
          const shipDate = staged.shipDate instanceof Date ? staged.shipDate : new Date(staged.shipDate);
          variables.shipDate = makeDateString(shipDate);
        }
        if (staged.publicationDate) {
          const pubDate = staged.publicationDate instanceof Date ? staged.publicationDate : new Date(staged.publicationDate);
          variables.publicationDate = makeDateString(pubDate);
        }
        if (staged.weight) variables.weight = parseFloat(staged.weight);
        if (staged.width) variables.width = parseFloat(staged.width);
        if (staged.height) variables.height = parseFloat(staged.height);
        if (staged.thickness) variables.thickness = parseFloat(staged.thickness);
        if (staged.pageCount) variables.pageCount = parseInt(staged.pageCount, 10);
        if (staged.adminNotes) variables.adminNotes = staged.adminNotes;
        if (staged.status && staged.status.id) variables.status = String(staged.status.id); // Changed: Keep as String per mutation def


        // Handle creators
        if (stagedCreators.length > 0) {
          variables.creatorIds = JSON.stringify(
            stagedCreators.map(c => ({
              creatorId: parseInt(c.creator.id, 10),
              onixCodeId: parseInt(c.onixContributorCode.id, 10)
            }))
          );
        }

        // Remove undefined values to avoid sending nulls for optional fields unnecessarily
        Object.keys(variables).forEach(key => variables[key] === undefined && delete variables[key]);

        console.log("Variables being sent:", variables); // Debugging log

        const results = await createProduct({ variables });

        console.log("Mutation Results:", results); // Log the full result

        // Check if the core createProduct data exists
        if (!results || !results.data || !results.data.createProduct) {
          throw new Error("Invalid response structure from createProduct mutation.");
        }

        const createProductPayload = results.data.createProduct;
        const newProductData = createProductPayload.product;
        const productId = newProductData ? newProductData.id : null;

        // Log details for debugging
        console.log("createProductPayload:", createProductPayload);
        console.log("newProductData:", newProductData);
        console.log("productId:", productId);

        // Check for product ID specifically if product data was expected
        if (!productId && newProductData !== null) { // Allow product to be explicitly null if intended by backend
            console.error("Product data exists in response, but product ID is missing.", newProductData);
            throw new Error("Product creation succeeded according to message, but product ID was missing in the response product object.");
        }
        // If product is null, maybe that's okay depending on backend logic? Log a warning.
        else if (!productId && newProductData === null){
             console.warn("Product object was null in the response. Continuing based on success message...");
             // Decide if you should throw an error or proceed carefully
             // For now, let's allow proceeding if message indicates success
             if (!createProductPayload.message || !createProductPayload.message.toLowerCase().includes("success")) {
                 throw new Error("Product object was null and message did not indicate success.");
             }
        }

        // Creator linking now happens within CreateProduct mutation if creatorIds are passed
        // Remove the separate createProductCreator calls

        // if (stagedCreators.length > 0) {
        //   await Promise.all(stagedCreators.map(creator =>
        //     createProductCreator({
        //       variables: {
        //         product: parseInt(productId),
        //         creator: parseInt(creator.creator.id),
        //         onixContributorCode: parseInt(creator.onixContributorCode.id)
        //       }
        //     })
        //   ));
        // }

        await props.handleRefetchProducts();

        toaster.success(results.data.createProduct.message || "New product created!", { duration: 10 });

        // Fire confetti
        const duration = 700;
        const end = Date.now() + duration;

        // Fire multiple bursts of confetti
        const frame = () => {
          confetti({
            particleCount: 10,
            angle: 90,
            spread: 100,
            origin: { x: 0.5, y: .8 }
          });

          if (Date.now() < end) {
            requestAnimationFrame(frame);
          }
        };
        frame();

        // Wait for animation
        await new Promise(resolve => setTimeout(resolve, duration));

        setStagedCreators([]);
        handleClearStaged();
        props.handleToggleNewMode();
      } catch (error) {
        // More informative error logging
        console.error("Error during product creation process:", error);
        toaster.danger(`Error creating product: ${error.message}`);
      }
    } else {
      toaster.danger("Please fix the errors before saving");
    }
  }

  const handleAttachOrUpdateCreator = (id, creator, code) => {
    let newObj = {
      id: id ? id : stagedCreators.length + 1,
      creator: creator,
      onixContributorCode: code
    }
    let newarr = stagedCreators;
    if (id) {
      newarr = newarr.filter(creator => creator.id !== id);
    }
    newarr = [newObj].concat(newarr);
    setStagedCreators(newarr);
  }

  const handleRemoveCreator = creatorId => {
    let newStaged = stagedCreators.filter(creator => creator.id !== creatorId);
    setStagedCreators(newStaged);
    let newDeleted = [creatorId].concat(deletedCreators);
    setDeletedCreators(newDeleted);
  }

  // Determine tooltip text based on context
  const tooltipText = props.productWorkId
    ? "Please make sure format, ISBN, and retail price are entered before saving."
    : "Please make sure title, format, ISBN, and retail price are entered before saving.";

  return (
    <Pane position="absolute" width="calc(100vw - 204px)">
      <Pane id="statement-form" elevation={3} backgroundColor="white" padding={16}>
        <Pane display="flex" flexDirection="column">
          <Pane display="flex" flexDirection="row" justifyContent="space-between" marginY={8}>
            <Paragraph size={500} fontWeight="bolder" >
              {props.productWorkId ? "Add New Format" : "New Product"}
            </Paragraph>
          </Pane>
          <Pane display="flex" flexDirection="row" width="calc(100vw - 204px)">
            <Pane>
              {/* <CreatorBox
            editing={true}
            onixGlobalData={onixGlobalData}
            productcreatorSet={stagedCreators}
            handleUpdateCreatorStaged={handleAttachOrUpdateCreator}
            handleDelete={handleRemoveCreator}
            creators={publisherGlobalData.publishercreators}
            productId={undefined}
            editMode={"edit"}
            newMode={true}
          /> */}
            </Pane>
            <ProductForm
              product={staged}
              onUpdate={handleUpdateStaged}
              handleUpdateCreatorStaged={handleAttachOrUpdateCreator}
              handleDeleteProductCreator={handleRemoveCreator}
              productcreatorSet={stagedCreators}
              editing={true}
              editMode="new"
              // Pass productWorkId to ProductForm so it can hide title/imprint/series fields if needed
              productWorkId={props.productWorkId}
            />

          </Pane>
        </Pane>
        <Pane display="flex" flexDirection="row" justifyContent="flex-end">
          <Tooltip content={tooltipText}><HelpIcon color="muted" marginX={8} /></Tooltip>
          <Button className="btn-save-cancel-txt-bold" disabled={!canSave} marginRight={16} onClick={e => {
            handleCreateProduct();
          }}>
            Save
          </Button>
          <Button className="btn-save-cancel-txt-bold" onClick={e => {
            handleClearStaged();
            props.handleToggleNewMode();
          }}>
            Cancel
          </Button>
          </Pane>
      </Pane>
    </Pane>
  )
}

export default NewProduct;
